import {
  ChangeDetectorRef,
  Component,
  EnvironmentInjector,
  inject,
  InjectionToken,
  OnInit,
  runInInjectionContext,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NB_AUTH_OPTIONS,
  NbAuthJWTToken,
  NbAuthResult,
  NbAuthService,
  NbLoginComponent,
  NbTokenService,
} from '@nebular/auth';
import { filter, from, map, merge, of, switchMap, tap } from 'rxjs';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { Auth, user, User } from '@angular/fire/auth';
import { AuthStrategies } from '@vmspdb/ui/domain';

export const LOGIN_SITE = new InjectionToken<string>('VMS_LOGIN_SITE');

@UntilDestroy()
@Component({
  selector: 'vmspdb-ui-common-login',
  templateUrl: './common-login.component.html',
  styles: [],
  standalone: false,
})
export class CommonLoginComponent extends NbLoginComponent implements OnInit {
  site = inject(LOGIN_SITE);
  private auth = inject(Auth);
  private injector = inject(EnvironmentInjector);
  private tokenService = inject(NbTokenService);
  private activatedRoute = inject(ActivatedRoute);

  // docs/e2e-backlog.md#bug-025 - read once from the `returnUrl` query param
  // set by AuthGuard/resetAuth$ when redirecting a signed-out visit to a
  // protected route, rather than selected from the store: the previous
  // setAuthReturnUrl/selectAuthReturnUrl round-trip raced resetAuth$'s own
  // navigation (traced root cause on that entry) and lost.
  private returnUrl?: string;

  // Set for the duration of a form-driven login()/loginWithGoogle() call.
  // Without this, a successful login also fires the ngOnInit "already
  // logged in" pipe below - it reacts to the same Firebase user() emission
  // - and both paths call handleResult(), racing two
  // setTimeout(navigateByUrl) calls against each other (BUG-025, Candidate
  // B). This flag is set synchronously before either async login call
  // starts, so it is already true by the time user()'s post-login emission
  // is evaluated, regardless of which of the two settles first.
  private isSubmittingLogin = false;

  constructor() {
    const service = inject(NbAuthService);
    const options = inject(NB_AUTH_OPTIONS) ?? {};
    const cd = inject(ChangeDetectorRef);
    const router = inject(Router);

    super(service, options, cd, router);
  }

  ngOnInit() {
    this.returnUrl =
      this.activatedRoute.snapshot.queryParamMap.get('returnUrl') ?? undefined;

    if (!this.auth) {
      return;
    }
    // Handle redirect when already logged in
    merge(
      of(this.auth.currentUser),
      runInInjectionContext(this.injector, () => user(this.auth)),
    )
      .pipe(
        untilDestroyed(this),
        filter((user) => !!user && !this.isSubmittingLogin),
        switchMap((user) => from((user as User).getIdTokenResult())),
        map(
          (idToken) =>
            new NbAuthJWTToken(idToken.token, idToken.signInProvider ?? ''),
        ),
        filter((authToken) => authToken.isValid()),
        switchMap((authToken) => {
          return this.tokenService.set(authToken);
        }),
        tap(() =>
          this.handleResult(new NbAuthResult(true), this.returnUrl ?? '/'),
        ),
      )
      .subscribe();
  }

  override login(): void {
    this.errors = [];
    this.messages = [];
    this.submitted = true;
    this.isSubmittingLogin = true;

    this.service
      .authenticate(this.strategy, this.user)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.isSubmittingLogin = false;
        this.handleResult(result, this.returnUrl);
      });
  }

  loginWithGoogle() {
    this.errors = [];
    this.messages = [];
    this.submitted = true;
    this.isSubmittingLogin = true;

    this.service
      .authenticate(AuthStrategies.Google)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.isSubmittingLogin = false;
        this.handleResult(result, this.returnUrl);
      });
  }

  handleResult(result: NbAuthResult, returnUrl?: string) {
    this.submitted = false;

    if (result.isSuccess()) {
      this.messages = result.getMessages();
    } else {
      this.errors = result.getErrors();
    }

    const redirect = returnUrl || result.getRedirect();
    if (redirect) {
      setTimeout(() => {
        return this.router.navigateByUrl(redirect);
      }, this.redirectDelay);
    }
    this.cd.detectChanges();
  }
}
